import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
  AreaChart, Area,
} from 'recharts';
import axios from 'axios';
import { API_DOMAIN, API_ROUTES, headers } from '../../Reusable_Components/Config/apiConfig';

// ── Palette ───────────────────────────────────────────────────────────────────
const COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#e87860', '#8b5cf6', '#0ea5e9'];

const ACCENTS = {
  blue:   { bg: '#dbeafe', fg: '#1d4ed8' },
  amber:  { bg: '#fef3c7', fg: '#d97706' },
  green:  { bg: '#dcfce7', fg: '#16a34a' },
  orange: { bg: '#fff0ed', fg: '#e87860' },
};

// ── Static monthly trend (preview until analytics endpoint is configured) ─────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
const mockTrend = MONTHS.map((month, i) => ({
  month,
  leads: 4 + i * 3,
  opportunities: 2 + i * 2,
  quotes: 1 + i,
}));

// ── Stat Card ─────────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, accent, icon, to }) => (
  <div className="stat-card" style={{ cursor: to ? 'pointer' : 'default' }}
    onClick={to ? () => window.location.href = to : undefined}>
    <div className="stat-card-icon" style={{ background: accent.bg, color: accent.fg }}>{icon}</div>
    <div className="stat-card-info">
      {value === null
        ? <div className="stat-card-loading" />
        : <div className="stat-card-value">{value !== undefined ? Number(value).toLocaleString() : '—'}</div>}
      <div className="stat-card-label">{label}</div>
    </div>
  </div>
);

// ── SVG icons ─────────────────────────────────────────────────────────────────
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const LeadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const TargetIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
  </svg>
);
const DocIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

// ── Custom tooltips ───────────────────────────────────────────────────────────
const BarTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#475569' }}>{payload[0].value.toLocaleString()} records</div>
    </div>
  );
};

const AreaTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 1 }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
};

// ── ReportingAnalytics ────────────────────────────────────────────────────────
const ReportingAnalytics = () => {
  const [stats, setStats] = useState({ customers: null, leads: null, opportunities: null, quotes: null, contacts: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [customers, leads, opportunities, quotes, contacts] = await Promise.allSettled([
        axios.get(API_DOMAIN + API_ROUTES.customer, { headers }),
        axios.get(API_DOMAIN + API_ROUTES.lead, { headers }),
        axios.get(API_DOMAIN + API_ROUTES.opportunity, { headers }),
        axios.get(API_DOMAIN + API_ROUTES.quote, { headers }),
        axios.get(API_DOMAIN + API_ROUTES.contact, { headers }),
      ]);
      const getCount = r => {
        if (r.status !== 'fulfilled') return null;
        const d = r.value.data;
        if (!d) return null;
        if (Array.isArray(d)) return d.length;
        if (typeof d.count === 'number') return d.count;
        if (d.data !== undefined) {
          const payload = d.data;
          if (Array.isArray(payload)) return payload.length;
          if (payload && typeof payload.count === 'number') return payload.count;
          if (payload && Array.isArray(payload.results)) return payload.results.length;
        }
        if (Array.isArray(d.results)) return d.results.length;
        return null;
      };
      setStats({
        customers: getCount(customers),
        leads: getCount(leads),
        opportunities: getCount(opportunities),
        quotes: getCount(quotes),
        contacts: getCount(contacts),
      });
      setLoading(false);
    };
    load();
  }, []);

  const barData = [
    { name: 'Contacts',      value: stats.contacts      ?? 0 },
    { name: 'Leads',         value: stats.leads         ?? 0 },
    { name: 'Opportunities', value: stats.opportunities ?? 0 },
    { name: 'Quotes',        value: stats.quotes        ?? 0 },
    { name: 'Customers',     value: stats.customers     ?? 0 },
  ];

  const pieData = barData.filter(d => d.value > 0);
  const totalRecords = barData.reduce((s, d) => s + d.value, 0);

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Analytics Overview</h1>
          <p className="page-subtitle">Pipeline health across all modules</p>
        </div>
        <div className="page-actions">
          <Link to="/reporting/report/list" className="btn btn-secondary btn-sm">Reports →</Link>
        </div>
      </div>

      {/* ── Metrics row ── */}
      <div className="stat-cards">
        <MetricCard label="Customers"     value={stats.customers}     accent={ACCENTS.blue}   icon={<UsersIcon />}  to="/customer/list" />
        <MetricCard label="Leads"         value={stats.leads}         accent={ACCENTS.amber}  icon={<LeadIcon />}   to="/lead/list" />
        <MetricCard label="Opportunities" value={stats.opportunities} accent={ACCENTS.green}  icon={<TargetIcon />} to="/opportunity/list" />
        <MetricCard label="Quotes"        value={stats.quotes}        accent={ACCENTS.orange} icon={<DocIcon />}    to="/quote/list" />
      </div>

      {/* ── Charts row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, marginBottom: 20 }}>

        {/* Pipeline Bar */}
        <div className="chart-card">
          <div className="chart-card-header">
            <span className="chart-card-title">Pipeline Volume</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {loading ? '…' : `${totalRecords.toLocaleString()} total records`}
            </span>
          </div>
          <div className="chart-card-body">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} margin={{ top: 4, right: 12, left: -10, bottom: 0 }} barSize={40}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<BarTip />} cursor={{ fill: '#f8fafc' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {barData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribution Donut */}
        <div className="chart-card">
          <div className="chart-card-header">
            <span className="chart-card-title">Distribution</span>
          </div>
          <div className="chart-card-body" style={{ padding: '16px 8px' }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={3}
                  >
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    formatter={(val, name) => [`${val} (${totalRecords ? Math.round(val / totalRecords * 100) : 0}%)`, name]}
                  />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state" style={{ padding: '48px 0' }}>
                <div className="empty-state-icon" style={{ fontSize: 28 }}>📊</div>
                <div className="empty-state-text">No data yet</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Pipeline Trend ── */}
      <div className="chart-card" style={{ marginBottom: 20 }}>
        <div className="chart-card-header">
          <span className="chart-card-title">Pipeline Trend</span>
          <span style={{
            fontSize: 11, color: 'var(--text-muted)',
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 99, padding: '2px 8px',
          }}>
            Preview — connect analytics endpoint to show live data
          </span>
        </div>
        <div className="chart-card-body">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={mockTrend} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gLead" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gOpp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gQuote" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#e87860" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#e87860" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<AreaTip />} />
              <Area type="monotone" dataKey="leads"         name="Leads"         stroke="#3b82f6" fill="url(#gLead)"  strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="opportunities" name="Opportunities" stroke="#22c55e" fill="url(#gOpp)"   strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="quotes"        name="Quotes"        stroke="#e87860" fill="url(#gQuote)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Quick links to sub-modules ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {[
          { label: 'Reports',           to: '/reporting/report/list',            icon: '📋' },
          { label: 'Scheduled Reports', to: '/reporting/scheduled-report/list',  icon: '🗓' },
          { label: 'Segmentations',     to: '/reporting/segmentation/list',      icon: '🎯' },
          { label: 'Trend Analysis',    to: '/reporting/trend/list',             icon: '📈' },
          { label: 'Forecasts',         to: '/reporting/forecast/list',          icon: '🔮' },
          { label: 'Filters',           to: '/reporting/filter/list',            icon: '⚙' },
        ].map(({ label, to, icon }) => (
          <Link key={to} to={to} style={{ textDecoration: 'none' }}>
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: '16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              transition: 'box-shadow 0.15s', boxShadow: 'var(--shadow-xs)',
              fontSize: 13, color: 'var(--text-main)', fontWeight: 500,
            }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-xs)'}
            >
              <span style={{ fontSize: 20 }}>{icon}</span>
              {label}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ReportingAnalytics;
